import React, { useState, useEffect, useMemo } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { FaSignOutAlt, FaChevronDown } from "react-icons/fa";
import { useSelector } from "react-redux";

// பர்மிஷன் ஹெல்பர் செயல்பாடுகளை இம்போர்ட் செய்தல்
import { getVisibleKeysForUser, filterSidebarByPermissions } from "../config/SidebarConfig";

const Sidebar = ({
  collapsed,
  setCollapsed,
  showMobileSidebar,
  toggleSidebar,
  activeModule,
  setActiveModule,
  mode,
}) => {
  const { portalName } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [openChild, setOpenChild] = useState(null);

  // Redux-லிருந்து லாகின் செய்த பயனர் விபரங்களைப் பெறுகிறோம்
  const user = useSelector((state) => state?.user?.user);

  // சப்-சைட்பாரை கொலாப்ஸ் செய்ய உதவும் ஸ்டேட்
  const [isPanelCollapsed, setIsPanelCollapsed] = useState(false);

  // பயனரின் பர்மிஷன் அடிப்படையில் மாட்யூல்களை வடிகட்டுதல்
  const filteredSidebar = useMemo(() => {
    if (!user) return [];
    const visibleKeysSet = getVisibleKeysForUser(user);
    return filterSidebarByPermissions(visibleKeysSet);
  }, [user]);

  // Settings பயன்முறையில் அனுமதி உள்ள மாட்யூல்களை மட்டுமே இடது பட்டியில் காட்டுதல்
  const sidebarModulesToRender = useMemo(() => {
    if (mode === "settings") {
      return filteredSidebar.filter((mod) => mod.settings && mod.settings.length > 0);
    }
    return filteredSidebar;
  }, [filteredSidebar, mode]);

  const buildPath = (p) => `/${portalName}/${p}`;

  const isActivePath = (path) => {
    if (!path) return false;
    const full = buildPath(path);
    return location.pathname === full || location.pathname.startsWith(full + "/");
  };

  // filteredSidebar கொண்டு ஆக்டிவ் மாட்யூலைத் தேடுதல்
  const findModuleForPath = () =>
    filteredSidebar.find(
      (m) =>
        (m.path && isActivePath(m.path)) ||
        m.submodules?.some(
          (s) =>
            (s.path && isActivePath(s.path)) ||
            s.children?.some((c) => c.path && isActivePath(c.path))
        ) ||
        m.settings?.some((set) => set.path && isActivePath(set.path))
    );

  // [திருத்தம்]: சப்-மாட்யூல்கள் இருந்தால் மட்டுமே activeModule-ஐ செட் செய்கிறோம், இல்லையெனில் null செய்கிறோம்
  useEffect(() => {
    const match = findModuleForPath();
    if (match) {
      const isSettingsPath = location.pathname.includes("/settings");
      const list = isSettingsPath ? match.settings : match.submodules;
      
      if (list && list.length > 0) {
        setActiveModule(match);
      } else {
        setActiveModule(null);
      }
    } else {
      setActiveModule(null);
    }
  }, [location.pathname, filteredSidebar]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const list =
      mode === "settings" ? activeModule?.settings : activeModule?.submodules;
    if (!list) return;
    const idx = list.findIndex((s) =>
      s.children?.some((c) => isActivePath(c.path))
    );
    setOpenChild(idx !== -1 ? idx : null);
  }, [location.pathname, activeModule, mode]); // eslint-disable-line react-hooks/exhaustive-deps

  const isActiveCoreIcon = (mod) => {
    if (mode === "settings") return activeModule?.key === mod.key;
    if (!activeModule) {
      if (mod.path) return isActivePath(mod.path);
      return mod.submodules?.some(
        (s) =>
          (s.path && isActivePath(s.path)) ||
          s.children?.some((c) => c.path && isActivePath(c.path))
      );
    }
    return activeModule.key === mod.key;
  };

  const handleLogout = async () => {
    try {
      toast.success("Logout successful!");
      localStorage.clear();
      sessionStorage.clear();
      navigate("/login", { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.message || "Logout failed. Please try again.");
    }
  };

  const firstLeafOf = (list) => {
    if (!list || list.length === 0) return null;
    const first = list[0];
    if (first.path) return { path: first.path, openIdx: null };
    if (first.children?.length) return { path: first.children[0].path, openIdx: 0 };
    return null;
  };

  const handleCoreClick = (mod) => {
    const list = mode === "settings" ? mod.settings : mod.submodules;
    const hasSubItems = list && list.length > 0;

    // [திருத்தம் 1]: சப்-மாட்யூல்கள் இல்லாத மாட்யூல்களுக்கான கண்டிஷன் (எ.கா: Home)
    if (!hasSubItems) {
      setActiveModule(null);
      setIsPanelCollapsed(false);
      if (mod.path) {
        navigate(buildPath(mod.path));
      }
      if (window.innerWidth < 1024) toggleSidebar();
      return;
    }

    // [திருத்தம் 2]: ஏற்கனவே ஆக்டிவாக உள்ள மாட்யூலை மீண்டும் கிளிக் செய்யும் போது கொலாப்ஸ் ஆகாமல் தடுத்தல்
    if (activeModule?.key === mod.key) {
      if (isPanelCollapsed) {
        setIsPanelCollapsed(false); // ஒருவேளை பேனல் மூடப்பட்டிருந்தால் மட்டுமே விரிவடையும்
      }
      // சப்-மாட்யூல்கள் இருப்பதால், மீண்டும் கிளிக் செய்தாலும் activeModule null ஆகாது (திறந்தே இருக்கும்)
      return;
    }

    setActiveModule(mod);
    setIsPanelCollapsed(false);
    setOpenChild(null);

    const leaf = firstLeafOf(list);
    if (leaf) {
      navigate(buildPath(leaf.path));
      setOpenChild(leaf.openIdx);
    }

    if (window.innerWidth < 1024) toggleSidebar();
  };

  const handleLeafClick = () => {
    if (window.innerWidth < 1024) toggleSidebar();
  };

  const hasActiveModule = !!activeModule;
  const panelMode = hasActiveModule && !isPanelCollapsed;

  const panelList =
    mode === "settings" ? activeModule?.settings : activeModule?.submodules;
  const panelTitle =
    mode === "settings"
      ? `${activeModule?.label} Settings`
      : activeModule?.label;

  const wrapperClasses = `pb-20 lg:pb-0 fixed lg:static z-50 h-full transition-all duration-300 ease-in-out ${
    showMobileSidebar ? "translate-x-0" : "-translate-x-full"
  } lg:translate-x-0 bg-white border-r border-gray-200 flex`;

  return (
    <div
      className={`${wrapperClasses} ${
        panelMode
          ? "w-[300px]"
          : hasActiveModule
          ? "w-20"
          : collapsed
          ? "w-20"
          : "w-56"
      }`}
    >
      {/* ---------- LEFT STRIP ---------- */}
      <div
        className={`flex flex-col flex-shrink-0 transition-all duration-300 ease-in-out ${
          hasActiveModule
            ? "w-20 border-r border-gray-100"
            : "w-full"
        }`}
      >
        <div className="flex-1 overflow-y-auto">
          {sidebarModulesToRender.map((mod) => {
            const active = isActiveCoreIcon(mod);
            return (
              <button
                key={mod.key}
                onClick={() => handleCoreClick(mod)}
                className={`group relative flex w-full p-3 cursor-pointer transition-all duration-200 ease-out ${
                  hasActiveModule
                    ? "flex-col items-center gap-1"
                    : collapsed
                    ? "justify-center"
                    : "items-center gap-4"
                } ${
                  active
                    ? hasActiveModule
                      ? "text-primary bg-gradient-to-l from-red-100 to-white font-semibold"
                      : "bg-primary text-white shadow-[0_8px_24px_rgba(0,0,0,0.07)]"
                    : "text-gray-700 hover:bg-gradient-to-l hover:from-red-100 hover:to-white hover:shadow-[0_8px_24px_rgba(0,0,0,0.07)]"
                }`}
                title={mod.label}
              >
                {hasActiveModule && (
                  <span
                    className={`absolute left-0 top-1 bottom-1 rounded-r transition-all duration-300 ${
                      active
                        ? "w-1 bg-primary"
                        : "w-0 group-hover:w-1 group-hover:bg-primary"
                    }`}
                  />
                )}

                <div
                  className={`w-5 h-5 flex-shrink-0 transition-all duration-300 ${
                    active
                      ? hasActiveModule
                        ? "bg-primary"
                        : "bg-white"
                      : "bg-gray-700 group-hover:bg-primary group-hover:scale-125"
                  }`}
                  style={{
                    WebkitMaskImage: `url(${mod.icon})`,
                    maskImage: `url(${mod.icon})`,
                    WebkitMaskSize: "contain",
                    maskSize: "contain",
                    WebkitMaskRepeat: "no-repeat",
                    maskRepeat: "no-repeat",
                    WebkitMaskPosition: "center",
                    maskPosition: "center",
                  }}
                />

                {!collapsed && (
                  <span
                    className={`text-[11px] font-medium transition-all duration-300 truncate group-hover:translate-x-0.5 ${
                      !hasActiveModule ? "text-sm" : ""
                    } ${active && !hasActiveModule ? "text-white" : ""}`}
                  >
                    {mod.label}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <button
          onClick={handleLogout}
          className={`flex w-full p-3 text-gray-700 cursor-pointer hover:text-primary hover:bg-red-50 transition-all duration-200 border-t border-gray-100 ${
            hasActiveModule
              ? "flex-col items-center gap-1"
              : collapsed
              ? "justify-center"
              : "items-center gap-4"
          }`}
          title="Log out"
        >
          <span className="text-xl text-primary">
            <FaSignOutAlt />
          </span>
          {!collapsed && <span className="text-xs font-medium">Log out</span>}
        </button>
      </div>

      {/* ---------- RIGHT PANEL ---------- */}
      <div
        className={`flex flex-col flex-shrink-0 transition-all duration-300 ease-in-out overflow-hidden ${
          panelMode ? "w-[220px] opacity-100" : "w-0 opacity-0"
        }`}
      >
        {panelMode && (
          <>
            <div className="px-4 py-5 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-base font-bold text-gray-900 whitespace-nowrap truncate">
                {panelTitle}
              </h2>
              <button
                onClick={() => setIsPanelCollapsed(true)}
                className="p-1 rounded-md text-gray-400 hover:text-primary hover:bg-red-50 transition-all duration-200"
                title="Collapse Panel"
              >
                <svg
                  className="w-5 h-5 transition-transform duration-200"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M11 19l-7-7 7-7M20 19l-7-7 7-7"
                  />
                </svg>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto py-2">
              {(!panelList || panelList.length === 0) && (
                <p className="px-4 py-4 text-sm text-gray-400">
                  No items available
                </p>
              )}

              {panelList?.map((sub, idx) => {
                const hasChildren = !!sub.children;
                const childActive = sub.children?.some((c) =>
                  isActivePath(c.path)
                );
                const directActive = isActivePath(sub.path);
                const isOpen = openChild === idx;

                if (hasChildren) {
                  return (
                    <div key={idx} className="relative">
                      <button
                        onClick={() => setOpenChild(isOpen ? null : idx)}
                        className={`group relative flex items-center justify-between w-full px-4 py-2.5 text-sm font-medium transition-all duration-300 ${
                          isOpen || childActive
                            ? "bg-gradient-to-l from-red-100 to-white text-primary"
                            : "text-gray-700 hover:bg-gradient-to-l hover:from-red-100 hover:to-white"
                        }`}
                      >
                        <span
                          className={`absolute right-0 top-1 bottom-1 rounded-l transition-all duration-300 ${
                            isOpen || childActive
                              ? "w-1 bg-primary"
                              : "w-0 group-hover:w-1 group-hover:bg-primary"
                          }`}
                        />
                        <span className="truncate pr-2">{sub.label}</span>
                        <FaChevronDown
                          className={`text-xs flex-shrink-0 transition-all duration-300 ${
                            isOpen
                              ? "rotate-180 text-primary"
                              : "text-gray-400 group-hover:text-primary"
                          }`}
                        />
                      </button>

                      <div
                        className={`grid transition-all duration-300 ease-in-out ${
                          isOpen
                            ? "grid-rows-[1fr] opacity-100"
                            : "grid-rows-[0fr] opacity-0"
                        } overflow-hidden`}
                      >
                        <div className="overflow-hidden">
                          <div className="py-1">
                            {sub.children.map((child) => {
                              const active = isActivePath(child.path);
                              return (
                                <div
                                  key={child.path}
                                  className="relative group flex items-center"
                                >
                                  <svg
                                    className="absolute left-4 top-0 h-full w-5 flex-shrink-0"
                                    viewBox="0 0 20 40"
                                    preserveAspectRatio="none"
                                    fill="none"
                                  >
                                    <path
                                      d="M2 0 V16 Q2 22 8 22 H18"
                                      stroke={active ? "#dc2626" : "#d1d5db"}
                                      strokeWidth="1.5"
                                    />
                                  </svg>
                                  <Link
                                    to={buildPath(child.path)}
                                    onClick={handleLeafClick}
                                    className={`relative w-full ml-7 mr-2 rounded-lg px-3 py-2 text-sm transition-all duration-300 truncate ${
                                      active
                                        ? "bg-gradient-to-l from-red-100 to-white text-primary font-semibold"
                                        : "text-gray-600 hover:bg-gradient-to-l hover:from-red-100 hover:to-white hover:text-primary"
                                    }`}
                                  >
                                    {child.label}
                                  </Link>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                }

                return (
                  <Link
                    key={sub.path}
                    to={buildPath(sub.path)}
                    onClick={handleLeafClick}
                    className={`group relative flex items-center px-4 py-2.5 text-sm font-medium transition-all duration-300 ${
                      directActive
                        ? "bg-gradient-to-l from-red-100 to-white text-primary"
                        : "text-gray-700 hover:bg-gradient-to-l hover:from-red-100 hover:to-white"
                    }`}
                  >
                    <span
                      className={`absolute right-0 top-1 bottom-1 rounded-l transition-all duration-300 ${
                        directActive
                          ? "w-1 bg-primary"
                          : "w-0 group-hover:w-1 group-hover:bg-primary"
                      }`}
                    />
                    <span className="group-hover:translate-x-0.5 transition-transform duration-300 truncate">
                      {sub.label}
                    </span>
                  </Link>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Sidebar;